import Button from '@components/Button';
import Image from '@components/Image';
import Modal from '@components/Modal';
import { sendError, sendWarning } from '@components/Noti';
import TextLink from '@components/TextLink';
import Title from '@components/Title';
import { THUMB_SIZE_LIMIT } from '@constants';
import { ImageType, type KrpPlayer, type SceneData, SceneType } from '@types';
import dataURLtoFile from '@utils/dataURLtoFile';
import selectFile from '@utils/selectFile';
// @ts-ignore
import { KrpanoPlayer } from 'pano-sdk';
import { formatSize } from 'pano-utils';
import { useEffect, useRef, useState } from 'react';
import { loadScene } from '../../player';
import Img0 from './img-0.png';
import Img1 from './img-1.png';
import Img2 from './img-2.png';
import Img3 from './img-3.png';

interface Props {
  sceneData: SceneData;
  thumbSrc: string;
  title?: string;
  onSceneChangeClick?: () => void;
  onRequestClose: () => void;
  onConfirm: (file: File) => void;
}

const items = [
  { id: 0, name: '正常', src: Img0 },
  { id: 1, name: '鱼眼', src: Img1 },
  { id: 2, name: '小行星', src: Img2 },
  { id: 3, name: '水晶球', src: Img3 },
] as const;

function ThumbReplaceModal({
  sceneData,
  thumbSrc,
  title = '场景封面设置',
  onSceneChangeClick,
  onRequestClose,
  onConfirm,
}: Props) {
  const playerRef = useRef<KrpPlayer | null>(null);
  const [snapshot, setSnapshot] = useState<string>();
  const [file, setFile] = useState<File>();

  const handleCaptureDone = (dataUrl: string) => {
    setSnapshot(dataUrl);
  };

  const handleCaptureError = () => {
    sendError('播放器截图错误，请重试');
  };

  useEffect(() => {
    if (!playerRef.current) {
      const player = new KrpanoPlayer() as KrpPlayer;
      player.createPlayer(
        'krp-thumb',
        {
          onSnapshootComplete: handleCaptureDone,
          onSnapshootError: handleCaptureError,
        },
        () => {
          playerRef.current = player;
          loadScene({ player, data: sceneData });
        },
        { enableSnapshoot: true }
      );
    }
    return () => {
      playerRef.current?.removePlayer();
    };
  }, []);

  const handleViewClick = (id: number) => () => {
    if (id === 0) {
      playerRef.current?.normalView();
    } else if (id === 1) {
      playerRef.current?.fisheyeView();
    } else if (id === 2) {
      playerRef.current?.littlePlanetView();
    } else if (id === 3) {
      playerRef.current?.ballView();
    }
  };

  const handleUpload = async () => {
    const files = await selectFile({
      accept: 'image/jpeg, image/png, image/gif',
    });
    const image = files[0];
    if (image.size > THUMB_SIZE_LIMIT) {
      sendWarning(`图片过大（不能大于${formatSize(THUMB_SIZE_LIMIT)})`);
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setSnapshot(e.target?.result as string);
      setFile(image);
    };
    reader.readAsDataURL(image);
  };

  const handleConfirm = () => {
    if (file) {
      onConfirm(file);
    } else if (snapshot) {
      onConfirm(dataURLtoFile(snapshot, 'capture.png'));
    }
    onRequestClose();
  };

  return (
    <Modal
      isOpen
      style={{ width: 800, height: 550 }}
      onRequestClose={onRequestClose}
      header={<Title>{title}</Title>}
      footer={<Button onClick={handleConfirm}>确认操作</Button>}
    >
      <div className="flex h-full items-center justify-around">
        <div className=" flex flex-col">
          <div className="mb-5 flex items-center font-medium">
            <div>
              {sceneData.type === SceneType.matrix
                ? '拖动矩阵以截取封面图'
                : '拖动全景以截取封面图'}
            </div>
            {!!onSceneChangeClick && (
              <TextLink
                className="ml-auto"
                onClick={() => {
                  onRequestClose();
                  onSceneChangeClick();
                }}
              >
                选择场景
              </TextLink>
            )}
          </div>
          <div className="relative flex h-300 w-300 justify-center overflow-hidden">
            <div
              style={{ width: 600, height: 600 }}
              className="absolute origin-top scale-50 transform"
            >
              <div id="krp-thumb" className="absolute inset-0" />
            </div>
            {sceneData.type !== SceneType.matrix && (
              <div className="absolute top-40 flex h-50 w-200 items-center justify-around rounded bg-black-black bg-opacity-50">
                {items.map((x) => (
                  <div
                    key={x.id}
                    className="flex cursor-pointer flex-col items-center text-sm text-white"
                    onClick={handleViewClick(x.id)}
                  >
                    <img className="mb-5 h-16 w-16" src={x.src} alt="" />
                    <div>{x.name}</div>
                  </div>
                ))}
              </div>
            )}
            <Button
              className="absolute bottom-30"
              onClick={() => {
                playerRef.current?.makeSnapshot();
              }}
            >
              截图
            </Button>
          </div>
        </div>
        <div className="flex flex-col">
          <div className="mb-5 font-medium">封面图预览</div>
          <div className="relative flex h-300 w-300 justify-center">
            <Image
              className="h-full w-full"
              src={snapshot ?? thumbSrc}
              type={snapshot ? ImageType.image : ImageType.thumb}
            />
            <div className="absolute bottom-30 flex items-center">
              <Button onClick={handleUpload}>从本地上传</Button>
              <div className="flex h-30 items-center justify-center rounded bg-black-black bg-opacity-50 px-10 text-sm text-white">
                尺寸：512 x 512
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default ThumbReplaceModal;
